import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

// Pirámide del campeonato: jugadores abajo y, al subir (octavos, cuartos,
// semifinal, final), bloques cada vez más grandes y algo rectangulares. Cada
// bloque se corre hacia el centro de la pirámide en vez de quedar sobre el punto
// medio de sus dos de abajo, así las dos mitades no parecen dos pirámides
// separadas: el conjunto cierra hacia la Final como un triángulo compacto.
// Crecimiento parejo: cada fila es el mismo factor más grande que la de abajo.
const SEAT_SIZE = 96;
const WIDTH_FACTOR = 1.27;
const HEIGHT_FACTOR = 1.12;
const GAP = 5;
const ROW_GAP = 26;
const TITLE_SPACE = 24;
const SEAT_LABEL_SPACE = 36;
// Cuánto se acerca cada bloque al centro (0 = sobre sus hijos, 1 = en el centro).
const PULL_TO_CENTER = 0.32;
// Margen dentro del bloque por donde puede entrar la línea que sube.
const ENTRY_MARGIN = 14;
const MAX_ZOOM = 4;
const DRAG_THRESHOLD = 6;

const ROUND_COLORS = {
  roundOf16: "#b84dff",
  quarterfinal: "#2f8bff",
  semifinal: "#ff3b3b",
  final: "#f2c230",
};
// Verde = partido jugado o en juego: la pirámide se va llenando de verde hasta la Final.
const PROGRESS_COLOR = "#39ff14";

// Jugadores (nivel 0) y partidos, cada ronda un poco más grande que la anterior.
const sizeOfLevel = (level) => ({
  width: Math.round(SEAT_SIZE * WIDTH_FACTOR ** level),
  height: Math.round(SEAT_SIZE * HEIGHT_FACTOR ** level),
});

// Fila con separación uniforme entre todos sus bloques, centrada en la pirámide.
// Se compacta hacia el centro, pero solo hasta donde la línea de cada bloque
// todavía puede entrar por su centro llegando desde sus dos bloques de abajo.
const MIN_BLOCK_GAP = 22;
const placeRowEvenly = (count, centerX, size, children, childSize) => {
  if (count < 2) return [centerX];
  const childPitch = children.length > 1 ? Math.abs(children[1].x - children[0].x) : childSize.width;
  const naturalPitch = 2 * childPitch;
  // El desvío se reparte entre la entrada de arriba y las salidas de abajo, así que
  // el bloque puede alejarse del punto medio de sus dos de abajo hasta el doble del
  // margen que admite cada uno.
  const maxShift = 2 * Math.min(size.width / 2 - ENTRY_MARGIN, childSize.width / 2 - ENTRY_MARGIN);
  const reachPitch = naturalPitch - (2 * maxShift) / (count - 1);
  const compactPitch = size.width + MIN_BLOCK_GAP;
  const target = naturalPitch * (1 - PULL_TO_CENTER) + compactPitch * PULL_TO_CENTER;
  const pitch = Math.max(compactPitch, reachPitch, target);
  return Array.from({ length: count }, (_, index) => centerX + (index - (count - 1) / 2) * pitch);
};

const ChampionshipBracket = ({ rounds, seats, roundTitles, renderMatch, renderSeat, cornerSlot = null }) => {
  const wrapperRef = useRef(null);
  const [available, setAvailable] = useState({ width: 0, height: 0 });

  // Cabe a lo ancho del panel y a lo alto de la ventana (sin desplazarse).
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper || typeof ResizeObserver !== "function") return undefined;
    const measure = () => {
      let scroller = wrapper.parentElement;
      while (scroller && scroller !== document.body && !/(auto|scroll)/.test(getComputedStyle(scroller).overflowY)) {
        scroller = scroller.parentElement;
      }
      const scrollTop = scroller && scroller !== document.body ? scroller.scrollTop : window.scrollY;
      const topInPage = wrapper.getBoundingClientRect().top + scrollTop;
      setAvailable({
        width: wrapper.clientWidth,
        height: Math.max(300, window.innerHeight - topInPage - 24),
      });
    };
    const observer = new ResizeObserver(measure);
    observer.observe(wrapper);
    window.addEventListener("resize", measure);
    measure();
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);

  const layout = useMemo(() => {
    const seatCount = Math.max(2, seats.length);
    const base = sizeOfLevel(0);
    const width = seatCount * base.width + (seatCount - 1) * GAP;
    const centerX = width / 2;
    const levels = rounds.length;

    // Row centers from the players (bottom) up to the Final (top).
    const rowHeights = Array.from({ length: levels + 1 }, (_, level) => sizeOfLevel(level).height);
    // Debajo de las fotos de la base van el número y el nombre del jugador.
    const height =
      TITLE_SPACE +
      rowHeights.reduce((sum, value) => sum + value, 0) +
      levels * (ROW_GAP + TITLE_SPACE) -
      TITLE_SPACE +
      SEAT_LABEL_SPACE;
    const centerY = [];
    let cursor = height - SEAT_LABEL_SPACE;
    for (let level = 0; level <= levels; level += 1) {
      centerY[level] = cursor - rowHeights[level] / 2;
      cursor -= rowHeights[level] + ROW_GAP + TITLE_SPACE;
    }

    const seatNodes = seats.map((entry, index) => ({
      entry,
      x: base.width / 2 + index * (base.width + GAP),
      y: centerY[0],
      size: base,
    }));
    let previous = seatNodes;
    const roundNodes = rounds.map((round, roundIndex) => {
      const level = roundIndex + 1;
      const size = sizeOfLevel(level);
      const xs = placeRowEvenly(round.matches.length, centerX, size, previous, previous[0]?.size || size);
      const nodes = round.matches.map((match, index) => {
        const children = [previous[index * 2], previous[index * 2 + 1]].filter(Boolean);
        const x = xs[index];
        // El desvío se reparte por igual: las dos salidas de abajo se corren hacia el
        // centro de la pirámide y la entrada de arriba se corre lo mismo desde su centro,
        // así ninguna se ve muy desviada y nunca entra por la esquina.
        const reachOf = (node) => node.size.width / 2 - ENTRY_MARGIN;
        const [leftChild, rightChild] = children;
        const midpoint = rightChild ? (leftChild.x + rightChild.x) / 2 : leftChild.x;
        // Reparto proporcional al ancho: salidas y entrada quedan igual de desviadas
        // respecto a su propio bloque.
        const childWidth = leftChild.size.width;
        const offset = x - midpoint;
        let exitShift = (offset * childWidth) / (childWidth + size.width);
        let entryShift = offset - exitShift;
        const childLimit = Math.min(...children.map(reachOf));
        const parentLimit = reachOf({ size });
        exitShift = Math.max(-childLimit, Math.min(childLimit, exitShift));
        entryShift = Math.max(-parentLimit, Math.min(parentLimit, entryShift));
        const entryX = x - entryShift;
        const exits = children.map((child) => child.x + exitShift);
        return { match, x, y: centerY[level], size, children, entryX, exits, straight: true };
      });
      previous = nodes;
      return { key: round.key, nodes };
    });

    // A row may spread wider than the base: shift everything so it all fits.
    const allNodes = [...seatNodes, ...roundNodes.flatMap((row) => row.nodes)];
    const minX = Math.min(...allNodes.map((node) => node.x - node.size.width / 2));
    const maxX = Math.max(...allNodes.map((node) => node.x + node.size.width / 2));
    const shift = -Math.min(0, minX);
    allNodes.forEach((node) => {
      node.x += shift;
    });
    return { width: Math.max(width, maxX) + shift, height, seatNodes, roundNodes };
  }, [rounds, seats]);

  const fitScale =
    available.width > 0
      ? Math.min(1, available.width / layout.width, available.height / layout.height)
      : 1;
  // Móvil en vertical: margen arriba y abajo para hacer zoom con holgura.
  const phonePortrait =
    typeof window !== "undefined" &&
    window.matchMedia?.("(max-width: 760px) and (orientation: portrait)")?.matches;
  const verticalMargin = phonePortrait ? 48 : 0;
  const viewHeight = layout.height * fitScale + verticalMargin * 2;
  const viewWidth = available.width || layout.width * fitScale;
  // Base position when not zoomed: centered in the panel.
  const baseX = Math.max(0, (viewWidth - layout.width * fitScale) / 2);

  // Zoom solo de la pirámide: rueda o Ctrl+rueda, pellizco con dos dedos, arrastrar
  // para moverse y botones + / − / Ajustar. Un toque corto sigue siendo un clic.
  const [view, setView] = useState({ zoom: 1, x: 0, y: 0 });
  const viewRef = useRef(view);
  viewRef.current = view;
  const pointersRef = useRef(new Map());
  const gestureRef = useRef(null);
  const suppressClickRef = useRef(false);
  const viewportRef = useRef(null);

  const clampView = useCallback(
    (next) => {
      const zoom = Math.min(MAX_ZOOM, Math.max(1, next.zoom));
      if (zoom === 1) return { zoom: 1, x: 0, y: 0 };
      const contentWidth = layout.width * fitScale * zoom;
      const contentHeight = layout.height * fitScale * zoom;
      const minX = Math.min(0, viewWidth - contentWidth);
      const minY = Math.min(0, viewHeight - contentHeight);
      return {
        zoom,
        x: Math.min(Math.max(next.x, minX - viewWidth * 0.25), viewWidth * 0.25),
        y: Math.min(Math.max(next.y, minY - viewHeight * 0.25), viewHeight * 0.25),
      };
    },
    [fitScale, layout.height, layout.width, viewHeight, viewWidth],
  );

  const zoomAt = useCallback(
    (factor, clientX, clientY) => {
      const rect = viewportRef.current?.getBoundingClientRect();
      const current = viewRef.current;
      const nextZoom = Math.min(MAX_ZOOM, Math.max(1, current.zoom * factor));
      if (nextZoom === current.zoom) return;
      const px = rect && clientX ? clientX - rect.left : viewWidth / 2;
      const py = rect && clientY ? clientY - rect.top : viewHeight / 2;
      // Keep the point under the cursor or fingers in place while zooming.
      const originX = current.zoom === 1 ? baseX : current.x;
      const originY = current.zoom === 1 ? verticalMargin : current.y;
      const ratio = nextZoom / current.zoom;
      setView(clampView({ zoom: nextZoom, x: px - (px - originX) * ratio, y: py - (py - originY) * ratio }));
    },
    [baseX, clampView, verticalMargin, viewHeight, viewWidth],
  );

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return undefined;
    const handleWheel = (event) => {
      event.preventDefault();
      zoomAt(event.deltaY < 0 ? 1.15 : 1 / 1.15, event.clientX, event.clientY);
    };
    viewport.addEventListener("wheel", handleWheel, { passive: false });
    return () => viewport.removeEventListener("wheel", handleWheel);
  }, [zoomAt]);

  const handlePointerDown = (event) => {
    pointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    const points = [...pointersRef.current.values()];
    const current = viewRef.current;
    gestureRef.current = {
      startPoints: points.map((point) => ({ ...point })),
      startView: {
        zoom: current.zoom,
        x: current.zoom === 1 ? baseX : current.x,
        y: current.zoom === 1 ? verticalMargin : current.y,
      },
      moved: false,
    };
  };
  const handlePointerMove = (event) => {
    if (!pointersRef.current.has(event.pointerId) || !gestureRef.current) return;
    pointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    const points = [...pointersRef.current.values()];
    const gesture = gestureRef.current;
    if (points.length >= 2 && gesture.startPoints.length >= 2) {
      const distance = (a, b) => Math.hypot(a.x - b.x, a.y - b.y) || 1;
      const ratio = distance(points[0], points[1]) / distance(gesture.startPoints[0], gesture.startPoints[1]);
      const rect = viewportRef.current.getBoundingClientRect();
      const cx = (points[0].x + points[1].x) / 2 - rect.left;
      const cy = (points[0].y + points[1].y) / 2 - rect.top;
      const nextZoom = Math.min(MAX_ZOOM, Math.max(1, gesture.startView.zoom * ratio));
      const scaleChange = nextZoom / gesture.startView.zoom;
      gesture.moved = true;
      setView(
        clampView({
          zoom: nextZoom,
          x: cx - (cx - gesture.startView.x) * scaleChange,
          y: cy - (cy - gesture.startView.y) * scaleChange,
        }),
      );
      return;
    }
    const start = gesture.startPoints[0];
    const dx = event.clientX - start.x;
    const dy = event.clientY - start.y;
    if (!gesture.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
    if (gesture.startView.zoom === 1) return;
    gesture.moved = true;
    setView(clampView({ ...gesture.startView, x: gesture.startView.x + dx, y: gesture.startView.y + dy }));
  };
  const handlePointerEnd = (event) => {
    pointersRef.current.delete(event.pointerId);
    if (gestureRef.current?.moved) suppressClickRef.current = true;
    if (pointersRef.current.size === 0) gestureRef.current = null;
  };
  // A drag or a pinch must not end up pressing a button inside a block.
  const handleClickCapture = (event) => {
    if (!suppressClickRef.current) return;
    suppressClickRef.current = false;
    event.preventDefault();
    event.stopPropagation();
  };

  const zoomed = view.zoom > 1;
  const scale = fitScale * view.zoom;

  return (
    <div className="xc-pyramid-wrap" ref={wrapperRef}>
      <div className="xc-zoom-tools">
        {/* Arriba a la izquierda, en espejo con los botones de zoom. */}
        <div className="xc-zoom-tools-left">{cornerSlot}</div>
        <button type="button" onClick={() => zoomAt(1.3)} aria-label="Acercar" title="Acercar">
          +
        </button>
        <button type="button" onClick={() => zoomAt(1 / 1.3)} aria-label="Alejar" title="Alejar" disabled={!zoomed}>
          −
        </button>
        <button type="button" onClick={() => setView({ zoom: 1, x: 0, y: 0 })} disabled={!zoomed}>
          Ajustar
        </button>
      </div>
      <div
        ref={viewportRef}
        className={`xc-pyramid-scroll${zoomed ? " is-zoomed" : ""}`}
        style={{ height: viewHeight }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerEnd}
        onPointerCancel={handlePointerEnd}
        onPointerLeave={(event) => event.pointerType === "mouse" && handlePointerEnd(event)}
        onClickCapture={handleClickCapture}
      >
        <div
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            transform: `translate(${zoomed ? view.x : baseX}px, ${zoomed ? view.y : verticalMargin}px)`,
          }}
        >
          <div
            className="xc-pyramid"
            style={{ width: layout.width, height: layout.height, transform: `scale(${scale})` }}
          >
            <svg className="xc-pyramid-lines" width={layout.width} height={layout.height} aria-hidden="true">
              {layout.roundNodes.map(({ key, nodes }) =>
                nodes.map((node) => {
                  const played = Boolean(node.match.winner) || Boolean(node.match.live);
                  const color = played ? PROGRESS_COLOR : ROUND_COLORS[key] || PROGRESS_COLOR;
                  const parentBottom = node.y + node.size.height / 2;
                  const childTop = node.children[0].y - node.children[0].size.height / 2;
                  // Los dos tramos que suben de abajo y el que entra arriba miden lo mismo.
                  const elbowY = (childTop + parentBottom) / 2;
                  return (
                    <g key={node.match.id} stroke={color} style={{ color }}>
                      {node.straight ? (
                        <line x1={node.entryX} y1={parentBottom} x2={node.entryX} y2={elbowY} />
                      ) : (
                        <polyline
                          fill="none"
                          points={`${node.x},${parentBottom} ${node.x},${(parentBottom + elbowY) / 2} ${node.entryX},${
                            (parentBottom + elbowY) / 2
                          } ${node.entryX},${elbowY}`}
                        />
                      )}
                      {node.children.map((child, index) => (
                        <polyline
                          key={index}
                          fill="none"
                          points={`${node.entryX},${elbowY} ${node.exits[index]},${elbowY} ${node.exits[index]},${
                            child.y - child.size.height / 2
                          }`}
                        />
                      ))}
                    </g>
                  );
                }),
              )}
            </svg>

            {layout.roundNodes.map(({ key, nodes }) => (
              <div
                key={`title-${key}`}
                className={`xc-pyramid-title xc-round-${key}${key === "roundOf16" ? " is-two-lines" : ""}`}
                style={{
                  top: nodes[0].y - nodes[0].size.height / 2 - (key === "roundOf16" ? 42 : 24),
                  left: 0,
                  width: layout.width,
                }}
              >
                {key === "roundOf16" ? (
                  // En dos líneas, estrecho, para que no choque con las conexiones.
                  <span>
                    OCTAVOS
                    <br />
                    DE FINAL
                  </span>
                ) : (
                  <span>{roundTitles[key] || key}</span>
                )}
              </div>
            ))}

            {layout.roundNodes.map(({ key, nodes }) =>
              nodes.map((node) => (
                <div
                  key={node.match.id}
                  className={`xc-square xc-round-${key}${node.match.winner || node.match.live ? " is-progress" : ""}`}
                  style={{
                    left: node.x - node.size.width / 2,
                    top: node.y - node.size.height / 2,
                    width: node.size.width,
                    height: node.size.height,
                  }}
                >
                  {renderMatch(node.match, key)}
                </div>
              )),
            )}

            {layout.seatNodes.map((node, index) => (
              <div
                key={`seat-${index}`}
                className="xc-square xc-seat-square"
                style={{
                  left: node.x - node.size.width / 2,
                  top: node.y - node.size.height / 2,
                  width: node.size.width,
                  height: node.size.height,
                }}
              >
                {renderSeat(node.entry, index)}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChampionshipBracket;
